import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import {
	Engine,
	EngineRubricSkill,
	EngineRubricSkillCreatePayload,
	EngineRubricSkillUpdatePayload,
} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class EngineService {
	private readonly BASE_PATH = 'engines';

	public selectedEngineRubricSkillBs$ = new BehaviorSubject<EngineRubricSkill | undefined>(
		undefined
	);
	public readonly selectedEngineRubricSkill$ = this.selectedEngineRubricSkillBs$.asObservable();

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public engineRubricSkillsBs$: BehaviorSubject<EngineRubricSkill[]> = new BehaviorSubject<
		EngineRubricSkill[]
	>([]);

	public engineRubricSkills$: Observable<EngineRubricSkill[]> =
		this.engineRubricSkillsBs$.pipe(
			filter(
				engineRubricSkills =>
					engineRubricSkills !== null && engineRubricSkills !== undefined
			)
		);

	constructor(private api: ApiService) {
		// Load engine_rubric_skill entries on init
		this.getEngineRubricSkills$().catch(error => {
			console.error('Error fetching engine rubric skills:', error);
			this.engineRubricSkillsBs$.next([]);
		});
	}

	public selectEngineRubricSkill(engineRubricSkill: EngineRubricSkill | undefined): void {
		this.selectedEngineRubricSkillBs$.next(engineRubricSkill);
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getEngines$(): Promise<Engine[]> {
		return this.withLoading(async () => {
			const engines = await firstValueFrom(this.api.get<Engine[]>(this.BASE_PATH + '/all'));
			return engines;
		});
	}

	public async getEngineByName$(name: string): Promise<Engine> {
		return this.withLoading(async () => {
			const engines = await firstValueFrom(this.api.get<Engine>(this.BASE_PATH + '/byName/' + name));
			return engines;
		});
	}

	public async getEngineRubricSkills$(
		engine_name?: string,
		rubric_code?: string
	): Promise<EngineRubricSkill[]> {
		return this.withLoading(async () => {
			const query = new URLSearchParams();

			if (engine_name) {
				query.append('engine_name', engine_name);
			}

			if (rubric_code) {
				query.append('rubric_code', rubric_code);
			}

			const queryString = query.toString();

			const url = queryString
				? `${this.BASE_PATH}/engine_rubric_skill?${queryString}`
				: `${this.BASE_PATH}/engine_rubric_skill`;

			const engineRubricSkills = await firstValueFrom(
				this.api.get<EngineRubricSkill[]>(url)
			);

			this.engineRubricSkillsBs$.next(engineRubricSkills);

			return engineRubricSkills;
		});
	}

	public async getEngineRubricSkill$(uuid: string): Promise<EngineRubricSkill> {
		return this.withLoading(async () => {
			const engineRubricSkill = await firstValueFrom(
				this.api.get<EngineRubricSkill>(`${this.BASE_PATH}/${uuid}`)
			);

			return engineRubricSkill;
		});
	}

	public async createEngineRubricSkill$(
		payload: EngineRubricSkillCreatePayload
	): Promise<EngineRubricSkill> {
		return this.withLoading(async () => {
			const engineRubricSkill = await firstValueFrom(
				this.api.post<EngineRubricSkill>(this.BASE_PATH, payload)
			);

			this.engineRubricSkillsBs$.next([
				...this.engineRubricSkillsBs$.value,
				engineRubricSkill,
			]);

			return engineRubricSkill;
		});
	}

	public async updateEngineRubricSkill$(
		uuid: string,
		payload: EngineRubricSkillUpdatePayload
	): Promise<EngineRubricSkill> {
		return this.withLoading(async () => {
			const updatedEngineRubricSkill = await firstValueFrom(
				this.api.put<EngineRubricSkill>(`${this.BASE_PATH}/${uuid}`, payload)
			);

			const currentEngineRubricSkills = this.engineRubricSkillsBs$.value;

			const index = currentEngineRubricSkills.findIndex(prs => prs.uuid === uuid);

			if (index !== -1) {
				currentEngineRubricSkills[index] = updatedEngineRubricSkill;
				this.engineRubricSkillsBs$.next([...currentEngineRubricSkills]);
			}

			return updatedEngineRubricSkill;
		});
	}

	public async deleteEngineRubricSkill$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete(`${this.BASE_PATH}/${uuid}`));

			this.engineRubricSkillsBs$.next(
				this.engineRubricSkillsBs$.value.filter(prs => prs.uuid !== uuid)
			);

			if (this.selectedEngineRubricSkillBs$.value?.uuid === uuid) {
				this.selectEngineRubricSkill(undefined);
			}
		});
	}
}
